<template>
  <div 
    class="watermark-container"
    :style="containerStyle"
    ref="watermarkContainer"
  >
    <canvas ref="watermarkCanvas" style="display: none;"></canvas>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, reactive, watch } from 'vue'

export default {
  name: 'Watermark',
  props: {
    // 水印文字
    text: {
      type: String,
      default: '水印文字'
    },
    // 文字颜色
    color: {
      type: String,
      default: 'rgba(0, 0, 0, 0.5)'
    },
    // 字体大小
    fontSize: {
      type: Number,
      default: 14
    },
    // 字体
    fontFamily: {
      type: String,
      default: 'sans-serif'
    },
    // 旋转角度
    rotate: {
      type: Number,
      default: -30
    },
    // 水印间距
    gap: {
      type: Number,
      default: 60
    },
    // 是否跟随滚动
    fixed: {
      type: Boolean,
      default: true
    },
    // 水印透明度
    opacity: {
      type: Number,
      default: 0.15
    }
  },
  setup(props) {
    // 响应式引用
    const watermarkContainer = ref(null)
    const watermarkCanvas = ref(null)
    const containerStyle = reactive({})
    
    // 创建水印
    const createWatermark = () => {
      if (!watermarkCanvas.value) return
      
      const canvas = watermarkCanvas.value
      const ctx = canvas.getContext('2d')
      const { text, fontSize, rotate, color, fontFamily, gap } = props

      // 设置字体
      ctx.font = `${fontSize}px ${fontFamily}`
      // 测量文字宽度
      const textWidth = ctx.measureText(text).width
      // 设置canvas尺寸，预留足够空间
      canvas.width = textWidth + gap * 2
      canvas.height = fontSize * 3 + gap * 2
      
      // 重新设置字体和样式
      ctx.font = `${fontSize}px ${fontFamily}`
      ctx.fillStyle = color
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      
      // 旋转画布
      ctx.translate(canvas.width / 2, canvas.height / 2)
      ctx.rotate((rotate * Math.PI) / 180)
      ctx.translate(-canvas.width / 2, -canvas.height / 2)
      
      // 绘制水印文字
      ctx.fillText(text, canvas.width / 2, canvas.height / 2)
      
      // 设置水印样式
      updateWatermarkStyle(canvas)
    }
    
    // 更新水印样式
    const updateWatermarkStyle = (canvas) => {
      const dataUrl = canvas.toDataURL()
      
      // 设置容器样式
      Object.assign(containerStyle, {
        position: props.fixed ? 'fixed' : 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 9999,
        backgroundImage: `url(${dataUrl})`,
        backgroundRepeat: 'repeat',
        // opacity: props.opacity
        opacity: 0.15
      })
    }
    
    // 更新水印大小
    const updateWatermarkSize = () => {
      createWatermark()
    }
    
    // 监听属性变化
    watch(
      () => [props.text, props.fontSize, props.rotate, props.color, props.fontFamily, props.gap, props.fixed, props.opacity],
      () => {
        createWatermark()
      },
      { deep: true }
    )
    
    // 生命周期钩子
    onMounted(() => {
      // 延迟执行以确保DOM已渲染
      setTimeout(() => {
        createWatermark()
      }, 100)
      // 监听窗口大小变化
      window.addEventListener('resize', updateWatermarkSize)
    })
    
    onUnmounted(() => {
      window.removeEventListener('resize', updateWatermarkSize)
    })
    
    return {
      watermarkContainer,
      watermarkCanvas,
      containerStyle
    }
  }
}
</script>

<style scoped>
.watermark-container {
  pointer-events: none;
}
</style>